import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { User } from '../models/User';

interface MessageComposerProps {
  recipient: User;
  relatedEntityId?: string;
  initialSubject?: string;
  initialContent?: string;
  onClose: () => void;
  onSend: (recipientId: string, content: string, relatedEntityId?: string) => void;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  recipient,
  relatedEntityId,
  initialSubject,
  initialContent,
  onClose,
  onSend
}) => {
  const [subject, setSubject] = useState(initialSubject || '');
  const [message, setMessage] = useState(initialContent || '');
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      await onSend(recipient.id, message, relatedEntityId);
      setMessage('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">
            Message to {recipient.fullName}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {initialSubject && (
            <div className="mb-4">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                id="subject"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Message subject"
              />
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              id="message"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={!message.trim()}
            >
              <Send className="mr-2 h-4 w-4" />
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessageComposer; 